/**
 * Le nom d'une version dans la liste de choix.
 *
 * Sorti de `App.tsx` pour être vérifiable — même raison que `pliage.ts` et `barreGrise.ts` :
 * ce nom est la SEULE chose qui distingue deux versions d'un même déboursé, et on ne le voit
 * qu'en ouvrant une liste déroulante sur un chantier réel.
 *
 * Ce qu'il porte, dans cet ordre : le numéro (« V2 ») parce que c'est ainsi que le conducteur
 * désigne sa version, le n° de déboursé et son rang, le libellé, D'OÙ ELLE VIENT quand le
 * libellé ne suffit pas, et son état.
 */
import type { DepoListe, VersionListe } from "@planif/moteur";

/** Toutes les versions de tous les déboursés d'un chantier, à plat. */
export type Choix = {
  readonly depo: DepoListe;
  readonly version: VersionListe;
};

export function libelleChoix({ depo, version }: Choix): string {
  const rang = depo.rang === 0 ? "déboursé initial" : `complémentaire n° ${depo.rang}`;
  const etat = version.figeeLe === null ? "modifiable" : "figée";
  // Le numéro EN TÊTE : c'est ici qu'on choisit sur quoi travailler, et « la V1 » est le nom
  // par lequel le conducteur la désigne. Le devis figé et la CE de suivi n'en portent pas.
  const numero = version.rang === null ? "" : `V${String(version.rang)} · `;
  return `${numero}${depo.numeroDepo} (${rang}) — ${version.libelle ?? version.type}${mentionOrigine(version)} · ${etat}`;
}

/**
 * « (issue de V2 test chris) », et rien quand la version porte son propre nom.
 *
 * La CE de suivi s'appelle « CE de suivi » sur tous les chantiers : son libellé est posé par
 * le circuit de validation, pas par le conducteur. Rien à l'écran ne disait donc DE QUELLE
 * copie de travail elle descend — or c'est la seule question qu'on lui pose (Chris, le
 * 24/08/2026 : « je ne peux pas identifier qu'il s'agit de la version de Chris et non de
 * celle de Régis »), et elle devient indécidable une fois la validation passée.
 *
 * Le serveur ne renseigne l'origine que là où le libellé est imposé : ici, on la met en
 * forme, on ne décide pas de qui en a une.
 */
function mentionOrigine(version: VersionListe): string {
  if (version.origine === null) return "";
  const numero = version.origine.rang === null ? "" : `V${String(version.origine.rang)} `;
  return ` (issue de ${numero}${version.origine.libelle ?? "une version sans nom"})`;
}
